const { PRODUCT_TILE_LOCATION } = require('../../../../../config/analytics');
const jsdocTypes = require('../../../../../jsdoc-types'); // eslint-disable-line no-unused-vars
const { default: getFrontConfig } = require('../../common/getFrontConfig');
const { arrify } = require('../../common/utils/arrayUtils');

const { LIST_NAMES } = require('../analyticsConst');

const FRONT_CONFIG = getFrontConfig();
const { context: ProductTileContexts } = FRONT_CONFIG.vue.BaseProductTile;

/** @enum {string} */
const ProductTileLocation = PRODUCT_TILE_LOCATION; // eslint-disable-line no-unused-vars

/**
 * A key-value map to transform the context of a product tile into the item
 * list related to it
 * @type {Object<jsdocTypes.BaseProductTileContext, string>}
 */
const tileContextToItemList = {
  [ProductTileContexts.crossSellingGrid]: LIST_NAMES.crossSellingGrid,
  [ProductTileContexts.homeShopTheLook]: LIST_NAMES.homeShopTheLook,
  [ProductTileContexts.minicartSlider]: LIST_NAMES.cartRecommendedProducts,
  [ProductTileContexts.minicartSliderEmptyCart]:
    LIST_NAMES.emptyCartRecommendedProducts,
  [ProductTileContexts.pdpRecommendedSlider]: LIST_NAMES.recommendedProducts,
  [ProductTileContexts.pdpSlider]: LIST_NAMES.completeTheLook,
  /** List cannot be defined only by the context */
  [ProductTileContexts.plpGrid]: false,
  /** List cannot be defined only by the context */
  [ProductTileContexts.plpGridFullWidth]: false,
  /** List cannot be defined only by the context */
  [ProductTileContexts.plpSliderBanner]: false,
  [ProductTileContexts.search]: LIST_NAMES.search,
  [ProductTileContexts.searchModal]: LIST_NAMES.search,
};

/**
 * Build string for category/subcategory list data
 * 'Category-BAGS'
 * 'Subcategory-BAGS/CROSSBODY BAGS'
 * @param {string|string[]} categoryNames The names of the categories
 * @returns {string} list name
 */
function buildItemListForCategoryNames(categoryNames) {
  const arrayOfCategoryNames = arrify(categoryNames);
  const listName =
    arrayOfCategoryNames.length > 1
      ? LIST_NAMES.subcategory
      : LIST_NAMES.category;

  return `${listName}-${arrayOfCategoryNames.join('/').toUpperCase()}`;
}

/**
 * Build string for category/subcategory list data
 * 'Category-BAGS'
 * 'Subcategory-BAGS/CROSSBODY BAGS'
 * @param {Object} categoryModel category model
 * @param {Object[]} categoryModel.parentCategories Array of categories
 * @returns {string} list name
 */
function buildItemListForCategory(categoryModel) {
  const categoryNames =
    categoryModel?.parentCategories?.map((cat) => cat.name) ?? [];
  return buildItemListForCategoryNames(categoryNames);
}

/**
 * Return specific item list for a tile context
 * @param {jsdocTypes.BaseProductTileContext} tileContext The context of a
 *  product tile
 * @return {string} The related item list name
 */
const buildItemListForProductTileContext = (
  tileContext,
  {
    validTileContexts = [
      ProductTileContexts.crossSellingGrid,
      ProductTileContexts.homeShopTheLook,
      ProductTileContexts.minicartSlider,
      ProductTileContexts.minicartSliderEmptyCart,
      ProductTileContexts.pdpRecommendedSlider,
      ProductTileContexts.pdpSlider,
      ProductTileContexts.search,
      ProductTileContexts.searchModal,
    ],
  } = {}
) => {
  let tileList;

  if (tileContext && validTileContexts.includes(tileContext)) {
    tileList = tileContextToItemList[tileContext];
  }

  return tileList;
};

/**
 * Return specific item list related to the location of a Product Tile (Ex:
 * viewed or clicked event)
 *
 * @param {ProductTileLocation} location The location of the product tile
 * @param {Object} options Optional params
 * @param {jsdocTypes.Category} [options.category] The model of the PLP category
 * @param {jsdocTypes.FullProduct} options.product The model of the target product
 * @return {string} The item list related to the event
 */
const buildItemListForProductTileLocation = (
  location,
  { category, product } = {}
) => {
  let tileList;
  const locations = PRODUCT_TILE_LOCATION;
  let categoryNames;

  switch (location) {
    case locations.cartRecommendedProducts:
      tileList = LIST_NAMES.cartRecommendedProducts;
      break;
    case locations.emptyCartRecommendedProducts:
      tileList = LIST_NAMES.emptyCartRecommendedProducts;
      break;
    case locations.crossSellingGrid:
      tileList = LIST_NAMES.crossSellingGrid;
      break;
    case locations.homeShopTheLook:
      tileList = LIST_NAMES.homeShopTheLook;
      break;
    case locations.pdpCompleteTheLook:
      tileList = LIST_NAMES.completeTheLook;
      break;
    case locations.pdpRecommendedProducts:
      tileList = LIST_NAMES.recommendedProducts;
      break;
    case locations.search:
    case locations.searchModal:
      tileList = LIST_NAMES.search;
      break;
    case locations.plpGrid:
    case locations.plpSliderBanner:
    default:
      // Use the category of the PLP
      categoryNames = category?.parentCategories?.map((cat) => cat.name) ?? [];

      if (categoryNames.length === 0) {
        // Use the category asigned to product as fallback
        categoryNames = [product.parentCategoryName, product.subCategoryName];
      }

      tileList = buildItemListForCategoryNames(categoryNames);
  }

  return tileList;
};

/**
 * Get list data from document.referrer
 * 'Direct' if there is no referrer, 'Other' otherwise
 * @returns {string} 'Direct' or 'Other'
 */
function buildItemListForReferrer() {
  const lastPath = /[^/]+(?=\/$|$)/;
  const referrer = lastPath.exec(document.referrer);
  return referrer ? LIST_NAMES.other : LIST_NAMES.direct;
}

/**
 * Get list data from query param byl_origin gift_guide
 * @returns {string} 'recommended_gift_guide' or 'Direct'
 */
function buildItemListForGiftGuide() {
  const urlParmas = new URLSearchParams(window.location.search);
  const bylOriginParam = urlParmas.get('byl_origin');
  return bylOriginParam && bylOriginParam === 'gift_guide'
    ? LIST_NAMES.recommendedGiftGuide
    : LIST_NAMES.direct;
}

module.exports = {
  buildItemListForCategory,
  buildItemListForProductTileContext,
  buildItemListForProductTileLocation,
  buildItemListForReferrer,
  buildItemListForGiftGuide,
};
